/**
 * Emulator mixed-mode guards — only for useEmulators === true.
 * Clears orphan persisted Auth sessions, probes Auth/Firestore emulators,
 * and detects Auth not actually on the emulator (mixed mode).
 */
import { signOut } from 'firebase/auth';

export const AUTH_EMULATOR_PORT = 9099;
export const FIRESTORE_EMULATOR_PORT = 8080;

const ORPHAN_AUTH_CODES = new Set([
  'auth/user-not-found',
  'auth/user-token-expired',
  'auth/invalid-user-token',
]);

const BANNER_ID = 'todoes-emulator-unreachable-banner';

/** Default reachability probe timeout (ms). */
const PROBE_TIMEOUT_MS = 2000;

/**
 * @param {string} host
 * @param {number} port
 * @returns {string}
 */
export function unreachableEmulatorMessage(host, port) {
  return (
    `Emulator-modus actief maar Auth/Firestore-emulator op ${host}:${port} ` +
    `is niet bereikbaar; start \`npm run emulators\``
  );
}

/**
 * @param {{ code?: string }} err
 * @returns {boolean}
 */
function isOrphanAuthError(err) {
  return Boolean(err && ORPHAN_AUTH_CODES.has(err.code));
}

/**
 * If a persisted user no longer exists in the Auth emulator, sign out so
 * anonymous login can start cleanly (no manual "Clear site data").
 *
 * @param {import('firebase/auth').Auth} auth
 * @returns {Promise<boolean>} true when signOut was called
 */
export async function clearOrphanEmulatorSession(auth) {
  if (typeof auth.authStateReady === 'function') {
    await auth.authStateReady();
  }

  const user = auth.currentUser;
  if (!user) return false;

  try {
    await user.getIdToken(true);
    return false;
  } catch (err) {
    if (!isOrphanAuthError(err)) return false;
    await signOut(auth);
    return true;
  }
}

/**
 * Short reachability probe (timeout ≤ 2s). Any HTTP response = reachable.
 *
 * @param {string} host
 * @param {number} port
 * @param {{ fetchImpl?: typeof fetch, timeoutMs?: number }} [options]
 * @returns {Promise<boolean>}
 */
export async function probeEmulator(
  host,
  port,
  { fetchImpl = globalThis.fetch, timeoutMs = PROBE_TIMEOUT_MS } = {},
) {
  if (typeof fetchImpl !== 'function') return false;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetchImpl(`http://${host}:${port}/`, {
      method: 'GET',
      signal: controller.signal,
      mode: 'cors',
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * After connectAuthEmulator, emulatorConfig must be set — otherwise Auth may
 * still talk to production while Firestore uses the emulator.
 *
 * @param {import('firebase/auth').Auth} auth
 * @param {string} host
 * @returns {boolean}
 */
export function assertAuthEmulatorConnected(auth, host) {
  if (auth?.emulatorConfig) return true;

  console.error(
    `[Firebase] Auth niet op de emulator terwijl emulator-modus actief is ` +
      `(verwacht ${host}:${AUTH_EMULATOR_PORT}). Firestore kan wél de emulator ` +
      `gebruiken — gemengde modus; accounts:lookup gaat naar productie.`,
  );
  return false;
}

/**
 * Visible banner in the browser (dev / emulator mode). No-op without document.
 *
 * @param {string} message
 */
export function showEmulatorUnreachableBanner(message) {
  if (typeof document === 'undefined') return;

  let el = document.getElementById(BANNER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = BANNER_ID;
    el.setAttribute('role', 'alert');
    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      zIndex: '2147483647',
      padding: '12px 16px',
      background: '#7f1d1d',
      color: '#fef2f2',
      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
      fontSize: '14px',
      lineHeight: '1.4',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
    });
    document.body?.prepend(el);
  }
  el.textContent = message;
}

/**
 * Orchestrates orphan clear + parallel probes + emulatorConfig check.
 * No-ops when useEmulators is false.
 *
 * @param {{
 *   auth: import('firebase/auth').Auth,
 *   host: string,
 *   useEmulators: boolean,
 *   fetchImpl?: typeof fetch,
 *   showBanner?: boolean,
 * }} options
 */
export async function runEmulatorStartupGuards({
  auth,
  host,
  useEmulators,
  fetchImpl = globalThis.fetch,
  showBanner = true,
}) {
  if (!useEmulators) return;

  assertAuthEmulatorConnected(auth, host);

  const [authOk, firestoreOk] = await Promise.all([
    probeEmulator(host, AUTH_EMULATOR_PORT, { fetchImpl }),
    probeEmulator(host, FIRESTORE_EMULATOR_PORT, { fetchImpl }),
  ]);

  if (!authOk) {
    const msg = unreachableEmulatorMessage(host, AUTH_EMULATOR_PORT);
    console.error(`[Firebase] ${msg}`);
    if (showBanner) showEmulatorUnreachableBanner(msg);
  }
  if (!firestoreOk) {
    const msg = unreachableEmulatorMessage(host, FIRESTORE_EMULATOR_PORT);
    console.error(`[Firebase] ${msg}`);
    if (showBanner) showEmulatorUnreachableBanner(msg);
  }

  // Only attempt orphan clear when Auth emulator looks reachable — otherwise
  // getIdToken would fail for network reasons and could wipe a valid e2e session.
  if (authOk) {
    await clearOrphanEmulatorSession(auth);
  }
}
